// Locator.tsx
import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

interface BoundingBox {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

interface LocatorProps {
  pointer: { x: number; y: number };
  hexTile: { col: number; row: number };
  boundingBox: BoundingBox;
}

// Fixed hex dimensions as used in your Canvas.tsx
const HEX_WIDTH = 300;
const HEX_HEIGHT = 260;

const Locator: React.FC<LocatorProps> = ({ pointer, hexTile, boundingBox }) => {
  const theme = useTheme();

  // Calculate hex tile dimensions from the bounding box.
  const tileCountX = boundingBox.maxCol - boundingBox.minCol + 1;
  const tileCountY = boundingBox.maxRow - boundingBox.minRow + 1;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        left: 16,
        backgroundColor: alpha(theme.palette.secondary.main, 0.7),
        backdropFilter: "blur(4px)",
        color: theme.palette.text.primary,
        padding: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 8px ${theme.palette.primary.main}`,
        pointerEvents: "none",
        fontSize: "0.75rem",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
        }}
      >
        {/* Px Pointer Column */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Px Pointer:
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography variant="caption">
              X: {Math.round(pointer.x)}
            </Typography>
            <br />
            <Typography variant="caption">
              Y: {Math.round(pointer.y)}
            </Typography>
          </Box>
        </Box>

        {/* Hex Pointer Column */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Hex Pointer:
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography variant="caption">X: {hexTile.col}</Typography>
            <br />
            <Typography variant="caption">Y: {hexTile.row}</Typography>
          </Box>
        </Box>

        {/* Bounding Box Column */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Bounding Box:
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography variant="caption">
              minRow: {boundingBox.minRow}
            </Typography>
            <br />
            <Typography variant="caption">
              maxRow: {boundingBox.maxRow}
            </Typography>
            <br />
            <Typography variant="caption">
              minCol: {boundingBox.minCol}
            </Typography>
            <br />
            <Typography variant="caption">
              maxCol: {boundingBox.maxCol}
            </Typography>
          </Box>
        </Box>

        {/* Map Dimensions Column */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Map Dimensions:
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography variant="caption">X: {tileCountX}</Typography>
            <br />
            <Typography variant="caption">Y: {tileCountY}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Locator;
